import React, { useEffect, useRef, useState } from 'react';
import { motion, useAnimation } from 'framer-motion';
import './aboutsigiriya.css';

function AboutSigiriya() {
  const containerRef = useRef(null);
  const [sliderX, setSliderX] = useState(50);
  const [currentIndex, setCurrentIndex] = useState(0);
  const controls = useAnimation();
  const [hasInteracted, setHasInteracted] = useState(false);

  const comparisons = [
    {
      before: require('../assets/images/then.jpg'),
      after: require('../assets/images/now.jpg'),
    },
    {
      before: require('../assets/images/then2.jpg'),
      after: require('../assets/images/now2.jpg'),
    },
    {
      before: require('../assets/images/then3.jpg'),
      after: require('../assets/images/now3.jpg'),
    },
  ];

  useEffect(() => {
    if (hasInteracted) return;
  
    const interval = setInterval(() => {
      controls.start({
        x: [0, -20, 20, -15, 15, -10, 10, 0],
        transition: { duration: 1 },
      });
    }, 2000);
  
    return () => clearInterval(interval);
  }, [controls, hasInteracted]);
  

  const updateSlider = (clientX) => {
    if (!hasInteracted) setHasInteracted(true); // stop shake
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const x = clientX - rect.left;
    const newX = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderX(newX);
  };
  

  const startMouseDrag = (e) => {
    e.preventDefault();
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', stopMouseDrag);
  };

  const onMouseMove = (e) => updateSlider(e.clientX);
  const stopMouseDrag = () => {
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', stopMouseDrag);
  };

  const onTouchMove = (e) => {
    if (e.touches.length > 0) updateSlider(e.touches[0].clientX);
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? comparisons.length - 1 : prev - 1));
    setSliderX(50);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === comparisons.length - 1 ? 0 : prev + 1));
    setSliderX(50);
  };

  const { before, after } = comparisons[currentIndex];

  return (
    <div className="bg-[#fdf4e4] py-1 px-4 md:px-16">
      <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12 mb-20">
        <div className="w-full md:w-1/2 compare-img">
          <div
            ref={containerRef}
            className="relative w-full h-72 md:h-96 overflow-hidden rounded-xl shadow-xl cursor-ew-resize touch-none"
            onMouseDown={startMouseDrag}
            onTouchMove={onTouchMove}
          >
            {/* After Image */}
            <img
              src={after}
              alt="After"
              draggable={false}
              className="w-full h-full object-cover absolute top-0 left-0"
            />

            {/* Before Image */}
            <div
              className="absolute top-0 left-0 h-full w-full z-10"
              style={{ clipPath: `inset(0 ${100 - sliderX}% 0 0)` }}
            >
              <img
                src={before}
                alt="Before"
                draggable={false}
                className="w-full h-full object-cover absolute top-0 left-0"
              />
            </div>

            {/* Framer Motion Line */}
            <motion.div
              animate={controls}
              className="absolute top-0 h-full w-1 bg-[#fdf4e4] z-10"
              style={{ left: `${sliderX}%`, transform: 'translateX(-50%)' }}
            >
              <div className="absolute top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-white shadow-lg mx-auto cursor-ew-resize"></div>
            </motion.div>
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-center gap-6 mt-6">
            <button
              onClick={handlePrev}
              className="flex items-center gap-2 bg-[#3f3a32] text-white px-5 py-2 rounded-full text-sm font-semibold shadow-md hover:scale-105 hover:bg-[#2b2824] transition-all duration-300"
            >
              <span className="text-lg">←</span> Previous
            </button>
            <button
              onClick={handleNext}
              className="flex items-center gap-2 bg-[#7c623f] text-white px-5 py-2 rounded-full text-sm font-semibold shadow-md hover:scale-105 hover:bg-[#664f31] transition-all duration-300"
            >
              Next <span className="text-lg">→</span>
            </button>
          </div>
        </div>

        {/* Text Content */}
        <div className="w-full md:w-1/2">
          <h2 className="text-2xl md:text-3xl font-bold text-[#3f3a32] mb-4 font-mortend headings">
            WHAT IS SIGIRIYA VR ?
          </h2>
          <p className="text-[#3f3a32] text-base md:text-lg leading-relaxed">
            <strong>Sigiriya VR</strong> prides itself on a forward-thinking approach to preserving history and cultural heritage through the innovative use of technology. This immersive experience goes beyond simply merging art with digital tools—it is a carefully crafted journey that brings the past to life. Every detail within the Sigiriya VR world is the result of extensive research, ensuring a <strong>high level of historical and archaeological accuracy.</strong> By blending storytelling, visual artistry, and academic rigor, Sigiriya VR offers users a unique opportunity to engage with Sri Lanka’s rich heritage in a modern, meaningful way. It’s history reimagined for a new generation.
          </p>
          <p className="text-[#3f3a32] text-base md:text-lg leading-relaxed mt-4 mobile">
            Sigiriya VR opens a portal to the past, much like a time machine, transporting users to the majestic heights of ancient Sri Lanka. It allows you to witness Sigiriya in all its former glory, filled with atmosphere, architectural splendor, and rich cultural detail. Enhanced by a powerful, immersive soundtrack and expertly narrated storytelling, Sigiriya VR offers an emotionally resonant journey through time. It stands as a heartfelt tribute—a love letter—to a forgotten era that shaped Sri Lanka’s historical and cultural identity.
          </p>
        </div>
      </div>
    </div>
  );
}

export default AboutSigiriya;
  